/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { ImageProps } from "@packages/framework";
import { Image } from "@packages/framework";
import { useCallback, useState } from "react";

const blurOverlayStyle: Interpolation = {
  position: "absolute",
  inset: 0,
  backgroundSize: "cover",
  backgroundPosition: "center",
  filter: "blur(4px)",
  transform: "scale(1.02)",
};

export type ImageWithBlurTransitionProps = ImageProps & {
  blurDataURL?: string;
  transitionDuration?: number;
};

export const ImageWithBlurTransition = ({
  blurDataURL,
  transitionDuration = 700,
  onLoad,
  ...imageProps
}: ImageWithBlurTransitionProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  // Ref a wrapper span and query the inner <img> to skip the blur
  // transition when the image is already cached (img.complete).
  const wrapperRefCallback = useCallback((el: HTMLElement | null) => {
    const img = el?.querySelector("img");
    if (img?.complete) setImageLoaded(true);
  }, []);

  return (
    <>
      <span ref={wrapperRefCallback} style={{ display: "contents" }}>
        <Image
          onLoad={(e) => {
            setImageLoaded(true);
            onLoad?.(e);
          }}
          {...imageProps}
        />
      </span>
      {blurDataURL && (
        <div
          css={[
            blurOverlayStyle,
            {
              backgroundImage: `url(${blurDataURL})`,
              transition: `opacity ${transitionDuration}ms ease-out`,
            },
            imageLoaded && { opacity: 0 },
          ]}
          aria-hidden
        />
      )}
    </>
  );
};
